<template>
  <div class="pt-20">

  <div class="w-full max-w-6xl mx-auto flex flex-col items-center">
    <div
        class="relative w-full overflow-x-visible"
        :class="{
        'h-[180px]': isMobile,
        'h-[260px]': isTablet,
        'h-[360px]': isDesktop,
        'h-[540px]': isLarge
      }"
    >
      <div class="w-full h-full flex items-center justify-center relative select-none">
        <div
            v-for="(item, idx) in normalizedItems"
            :key="idx"
            :style="getSlideStyle(idx)"
            class="absolute left-1/2 top-1/2 transition-all duration-500"
            :class="[
            'rounded-2xl shadow-2xl cursor-pointer flex items-center justify-center bg-black',
            idx === currentIndex
              ? 'z-30 scale-110'
              : 'z-10 opacity-60'
          ]"
            @click="handleClick(idx)"
        >
          <component
              v-if="item"
              :is="item.type === 'youtube' ? 'iframe' : 'img'"
              v-bind="getMediaProps(item, idx === currentIndex)"
              class="rounded-2xl object-cover w-full h-full aspect-[16/9] bg-black"
          />
        </div>
      </div>
    </div>
    <!-- SETAS -->
    <div class="pt-10 mx-10 space-x-6 w-full flex items-center justify-center pointer-events-none z-40">
      <button
          @click="prevSlide"
          v-if="normalizedItems.length > 1"
          class="pointer-events-auto bg-black/70 text-white p-3 rounded-full hover:scale-110 transition shadow-lg ml-2"
          aria-label="Anterior"
      >
        <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <button
          @click="nextSlide"
          v-if="normalizedItems.length > 1"
          class="pointer-events-auto bg-black/70 text-white p-3 rounded-full hover:scale-110 transition shadow-lg mr-2"
          aria-label="Próximo"
      >
        <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  images: {
    type: Array,
    required: true,
  },
});
const currentIndex = ref(0);

// Responsivo
const isMobile = ref(false);
const isTablet = ref(false);
const isDesktop = ref(false);
const isLarge = ref(false);

function checkDevice() {
  const w = window.innerWidth;
  isMobile.value = w < 640;
  isTablet.value = w >= 640 && w < 1024;
  isDesktop.value = w >= 1024 && w < 1400;
  isLarge.value = w >= 1400;
}
onMounted(() => {
  checkDevice();
  window.addEventListener('resize', checkDevice);
});
onUnmounted(() => {
  window.removeEventListener('resize', checkDevice);
});

const normalizedItems = computed(() =>
    Array.isArray(props.images)
        ? props.images.filter(Boolean).map(item => (typeof item === 'string' ? { type: 'image', src: item } : item))
        : []
);

const prevSlide = () => {
  if (normalizedItems.value.length === 0) return;
  currentIndex.value = (currentIndex.value - 1 + normalizedItems.value.length) % normalizedItems.value.length;
};
const nextSlide = () => {
  if (normalizedItems.value.length === 0) return;
  currentIndex.value = (currentIndex.value + 1) % normalizedItems.value.length;
};
const handleClick = (idx) => {
  if (idx !== currentIndex.value) currentIndex.value = idx;
};

function getBaseSizes() {
  // Menor largura para mobile, nunca vaza do viewport
  const vw = Math.min(window.innerWidth, 640);
  if (isMobile.value) return { width: vw - 32, height: (vw - 32) * 9 / 16, gap: 48 }; // gap reduzido!
  if (isTablet.value) return { width: 440, height: 248, gap: 110 };
  if (isDesktop.value) return { width: 640, height: 360, gap: 180 };
  return { width: 960, height: 540, gap: 240 };
}

function getSlideStyle(idx) {
  const total = normalizedItems.value.length;
  if (total === 0) return { display: 'none' };
  const delta = idx - currentIndex.value;
  let realDelta = delta;
  if (delta > total / 2) realDelta = delta - total;
  if (delta < -total / 2) realDelta = delta + total;

  const { width: baseWidth, height: baseHeight, gap } = getBaseSizes();

  if (isMobile.value && Math.abs(realDelta) > 1) {
    return { opacity: 0, pointerEvents: 'none', display: 'none' };
  }

  const scaleStep = isMobile.value ? 0.22 : 0.16;
  const rotateStep = isMobile.value ? 9 : 19;
  const blurStep = isMobile.value ? 1.0 : 2.0;

  if (realDelta === 0) {
    return {
      transform: `translate(-50%, -50%) scale(1.06) rotateY(0deg)`,
      left: '50%',
      top: '50%',
      width: `${baseWidth}px`,
      height: `${baseHeight}px`,
      zIndex: 30,
      filter: 'none',
      maxWidth: '96vw',
      aspectRatio: '16/9',
      background: '#000'
    };
  }

  const side = realDelta < 0 ? -1 : 1;
  const absDelta = Math.abs(realDelta);

  return {
    transform: `
      translate(-50%, -50%)
      translateX(${side * gap * absDelta}px)
      scale(${1 - scaleStep * absDelta})
      rotateY(${side * rotateStep * absDelta}deg)
    `,
    left: '50%',
    top: '50%',
    width: `${baseWidth - 80 * absDelta}px`,
    height: `${baseHeight - 40 * absDelta}px`,
    zIndex: 30 - absDelta,
    filter: `blur(${blurStep * (absDelta - 0.4)}px)`,
    opacity: absDelta > (isMobile.value ? 1 : 2) ? 0 : 1,
    pointerEvents: absDelta > (isMobile.value ? 1 : 2) ? 'none' : 'auto',
    maxWidth: '90vw',
    aspectRatio: '16/9',
    background: '#000'
  };
}

function getMediaProps(item, isBig = false) {
  if (!item) return {};
  if (item.type === 'youtube') {
    const id = item.src.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
    const embed = id ? `https://www.youtube.com/embed/${id[1]}` : '';
    return {
      src: embed,
      frameborder: '0',
      allowfullscreen: true,
      class: 'aspect-[16/9] w-full h-full'
    };
  }
  return {
    src: item.src,
    alt: 'Imagem do carrossel',
    class: 'object-cover w-full h-full aspect-[16/9]'
  };
}
</script>

<style scoped>
/* Garante que slides não saiam do container no mobile */
.relative > .w-full {
  overflow-x: visible !important;
  overflow-y: visible !important;
}
</style>
